import {
  CheckCircleTwoTone,
  PauseCircleTwoTone,
  PlusOutlined,
  ControlOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Table, type TableProps } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useFeatures } from '@client/hooks/useFeatures';
import { usePrompt } from '@client/hooks/usePrompt';
import { useTable } from '@client/hooks/useTable';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/features/columns.metadata';
import { useFeatureQueries } from '@client/queries/useFeatureQueries';
import {
  subscriptionKeys,
  useSubscriptionQueries,
} from '@client/queries/useSubscriptionQueries';
import {
  getSubscriptionsByFeature,
  syncFeatureSubscriptions,
} from '@client/services/subscription.service';
import { useAssigned, assignStore } from '@client/store/assigned.store';
import { useDirty } from '@client/store/dirty.store';
import { useDrawerActions } from '@client/store/drawer.store';
import { useLoading } from '@client/store/loading.store';
import { useLocale } from '@client/store/locale.store';
import { successSaveMsg, errorSaveMsg } from '@client/utils/message.util';
import { indexable } from '@client/utils/table/table.util';
import { EStatus, type IFeature, type ISubscription } from '@common/types';

import { SubscriptionsDrawerContent } from './feature/panels/SubscriptionsDrawerContent';
import styles from './features.module.less';

import type { TColumns } from '@client/types/antd.type';
import type { ItemType } from 'antd/es/menu/interface';

export const FeaturesPage: React.FC = () => {
  const { ability } = useAuth();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { modalApi, messageApi } = useContext(NotificationContext);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedFeatures, setEditedFeatures] = useState<
    Record<string, Partial<IFeature>>
  >({});
  const [editedSubscriptions, setEditedSubscriptions] = useState<
    Record<string, ISubscription[]>
  >({});
  const [activeEditingFeatureId, setActiveEditingFeatureId] = useState<
    string | null
  >(null);

  const originalAssignmentsRef = useRef<Record<string, ISubscription[]>>({});

  const { setDirty } = useDirty();
  const { loading: globalLoading, setLoading } = useLoading();
  const { setOpenDrawer, setConfigDrawer, setOnRefreshDrawer } =
    useDrawerActions();

  const {
    loading: loadingFeatures,
    entities,
    total,
    refetch,
    onDeactivate,
    onEdit,
    onBulkToggleActive,
  } = useFeatures();

  const { useActiveSubscriptions } = useSubscriptionQueries();
  const { refetch: refetchActiveSubscriptions } = useActiveSubscriptions();

  const { useUpdateFeature } = useFeatureQueries();
  const updateFeatureMutation = useUpdateFeature();

  const handleRefreshDrawer = useCallback(async () => {
    await refetchActiveSubscriptions();
  }, [refetchActiveSubscriptions]);

  const { setAssigned: setAssignedSubscriptions } =
    useAssigned<ISubscription>();

  usePrompt();

  useEffect(() => {
    setAssignedSubscriptions([]);
  }, [setAssignedSubscriptions]);

  useEffect(() => {
    setOnRefreshDrawer(handleRefreshDrawer);
  }, [handleRefreshDrawer, setOnRefreshDrawer]);

  const hasChanges = useMemo(
    () =>
      Object.keys(editedFeatures).length > 0 ||
      Object.keys(editedSubscriptions).length > 0,
    [editedFeatures, editedSubscriptions],
  );

  useEffect(() => {
    setDirty(hasChanges);
    return () => {
      setDirty(false);
    };
  }, [hasChanges, setDirty]);

  useEffect(() => {
    if (!activeEditingFeatureId) return;

    const unsubscribe = assignStore.subscribe((state) => {
      const original =
        originalAssignmentsRef.current[activeEditingFeatureId] ?? [];
      const current = state.assigned as ISubscription[];

      const originalIds = original
        .map((s) => s.id)
        .sort()
        .join(',');
      const currentIds = current
        .map((s) => s.id)
        .sort()
        .join(',');

      if (originalIds !== currentIds) {
        setEditedSubscriptions((prev) => ({
          ...prev,
          [activeEditingFeatureId]: current,
        }));
      } else {
        setEditedSubscriptions((prev) => {
          const next = { ...prev };
          delete next[activeEditingFeatureId];
          return next;
        });
      }
    });

    return () => unsubscribe();
  }, [activeEditingFeatureId]);

  const handleFeatureChange = useCallback(
    (id: string, field: keyof IFeature, value: any) => {
      setEditedFeatures((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          [field]: value,
        },
      }));
    },
    [],
  );

  const handleAssignClick = useCallback(
    async (feature: IFeature) => {
      setActiveEditingFeatureId(feature.id);

      let currentAssignments = editedSubscriptions[feature.id as string];
      if (!currentAssignments) {
        try {
          currentAssignments = await getSubscriptionsByFeature(
            feature.id as string,
          );
        } catch (e) {
          currentAssignments = [];
        }
      }

      originalAssignmentsRef.current[feature.id as string] = currentAssignments;
      setAssignedSubscriptions(currentAssignments);

      setOpenDrawer(true);
      setConfigDrawer({
        size: 600,
        children: <SubscriptionsDrawerContent />,
        title: (
          <div className={styles.chipLabel}>
            <ControlOutlined />
            {t('subscriptions')}
          </div>
        ),
      });
    },
    [
      editedSubscriptions,
      setAssignedSubscriptions,
      setOpenDrawer,
      setConfigDrawer,
      t,
    ],
  );

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<IFeature>(entities, total);

  const assignDisabled = ability.cannot(
    EAbilityActions.ASSIGN,
    `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.SUBSCRIPTIONS}`,
  );

  const columns: TableProps<IFeature>['columns'] = React.useMemo(
    () =>
      columnsMetadata({
        locale,
        filteredInfo,
        sortedInfo,
        onDelete: onDeactivate,
        onEdit,
        entities,
        isEditing,
        editedFeatures,
        editedSubscriptions,
        onFeatureChange: handleFeatureChange,
        onAssignClick: handleAssignClick,
        assignDisabled,
      }),
    [
      locale,
      filteredInfo,
      sortedInfo,
      onDeactivate,
      onEdit,
      entities,
      isEditing,
      editedFeatures,
      editedSubscriptions,
      handleFeatureChange,
      handleAssignClick,
      assignDisabled,
    ],
  );

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<IFeature>(columns as TColumns<IFeature>[], [
      'createdAt',
      'updatedAt',
    ]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const featureUpdates = Object.keys(editedFeatures).map(async (id) => {
        const existingFeature = entities.find((f) => f.id === id);
        if (!existingFeature) return;
        await updateFeatureMutation.mutateAsync({
          id,
          data: { ...existingFeature, ...editedFeatures[id] } as IFeature,
        });
      });

      const subscriptionUpdates = Object.keys(editedSubscriptions).map(
        async (id) => {
          await syncFeatureSubscriptions({
            featureId: id,
            subscriptions: editedSubscriptions[id],
          });
        },
      );

      await Promise.all([...featureUpdates, ...subscriptionUpdates]);

      await queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.all, 'byFeature'],
      });

      await successSaveMsg(messageApi, true, t('feature'));
      setEditedFeatures({});
      setEditedSubscriptions({});
      originalAssignmentsRef.current = {};
      setIsEditing(false);
      setDirty(false);
    } catch (error) {
      await errorSaveMsg(messageApi, true, t('feature'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      modalApi.confirm({
        title: t('feature.edit.cancel.confirm.title', {
          defaultValue: 'Discard Changes',
        }),
        content: t('feature.edit.cancel.confirm.content', {
          defaultValue: 'Are you sure you want to discard your edits?',
        }),
        okText: t('actions.yes', { defaultValue: 'Yes' }),
        cancelText: t('actions.no', { defaultValue: 'No' }),
        onOk: () => {
          setEditedFeatures({});
          setEditedSubscriptions({});
          originalAssignmentsRef.current = {};
          setIsEditing(false);
          setDirty(false);
        },
      });
    } else {
      setIsEditing(false);
    }
  };

  const handleEditToggle = async () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      if (hasChanges) {
        await handleSave();
      } else {
        setIsEditing(false);
      }
    }
  };

  const selectedFeatures = entities.filter((f) =>
    selectedRowKeys.includes(f.id as string),
  );

  const allSelectedActive =
    selectedFeatures.length > 0 &&
    selectedFeatures.every((f) => f.status === EStatus.ACTIVE);
  const isBulkActivate = !allSelectedActive;

  const handleBulkToggle = () => {
    const confirmMessage = isBulkActivate
      ? selectedRowKeys.length > 1
        ? t('feature.msg.bulkActivateConfirm', {
            defaultValue: 'Do you want to activate these features?',
          })
        : t('feature.msg.activateConfirm', {
            defaultValue: 'Do you want activate thsi feature ',
          })
      : selectedRowKeys.length > 1
        ? t('feature.msg.bulkDeactivateConfirm', {
            defaultValue: 'Do you want diactivate the features ',
          })
        : t('feature.msg.deactivateConfirm', {
            defaultValue: 'Do you want to deactivate this feature?',
          });

    const actionText = isBulkActivate
      ? t('actions.activate')
      : t('actions.deactivate');

    modalApi.confirm({
      title: actionText,
      content: confirmMessage,
      okText: actionText,
      cancelText: t('actions.cancel'),
      okButtonProps: {
        danger: !isBulkActivate,
      },
      onOk: async () => {
        await onBulkToggleActive(selectedFeatures, isBulkActivate);
        setSelectedRowKeys([]);
        await refetch();
      },
    });
  };

  const tableProps = {
    columns: filteredColumns as TColumns<IFeature>[],
    pagination: {
      ...pagination,
    },
    scroll: { x: 1800 },
    bordered: true,
    size: 'small' as const,
    className: styles.gridList,
    dataSource: indexable<IFeature>(entities),
    loading: loadingFeatures || globalLoading,
    rowKey: (record: IFeature) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    rowSelection: isEditing
      ? undefined
      : {
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
        },
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<IFeature[]>
          onRefresh={refetch}
          entities={entities}
          subject={EAbilitySubjects.FEATURES}
          items={
            [
              !isEditing && {
                disabled: ability.cannot(
                  EAbilityActions.CREATE,
                  EAbilitySubjects.FEATURE,
                ),
                label: t('actions.add', { type: t('feature') }),
                icon: <PlusOutlined />,
                onClick: () => navigate('/features/new'),
              },
              !isEditing && {
                disabled:
                  selectedRowKeys.length === 0 ||
                  ability.cannot(
                    EAbilityActions.UPDATE,
                    EAbilitySubjects.FEATURE,
                  ),
                label:
                  selectedRowKeys.length > 0
                    ? isBulkActivate
                      ? t('actions.activate')
                      : t('actions.deactivate')
                    : `${t('actions.activate')} / ${t('actions.deactivate')}`,
                icon:
                  selectedRowKeys.length > 0 && !isBulkActivate ? (
                    <PauseCircleTwoTone />
                  ) : (
                    <CheckCircleTwoTone />
                  ),
                onClick: handleBulkToggle,
              },
              {
                disabled: globalLoading,
                label: isEditing
                  ? hasChanges
                    ? t('actions.update')
                    : t('actions.cancel')
                  : t('actions.editTable', { defaultValue: 'Edit Table' }),
                icon: isEditing ? (
                  hasChanges ? (
                    <SaveOutlined />
                  ) : (
                    <CloseOutlined />
                  )
                ) : (
                  <EditOutlined />
                ),
                onClick: handleEditToggle,
              },
              isEditing &&
                hasChanges && {
                  disabled: globalLoading,
                  label: t('actions.cancel'),
                  icon: <CloseOutlined />,
                  onClick: handleCancelEdit,
                },
            ].filter(Boolean) as unknown as ItemType[]
          }
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: total })}
      </div>
    ),
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.FEATURES}
      title={<PageTitle name={t('features')} />}
    >
      <Table<IFeature> {...tableProps} />
    </PageLayout>
  );
};
